import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { IModalContext } from '../../../../models';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	useAutomationTemplateSetStatusMutation,
	useCancelAllInProgressAutomationsMutation,
} from '../../../../queries/Automation';
import { styleSheet } from './styles';

interface IProps extends IModalContext {
	automationTemplate: Api.IAutomationTemplateReference;
	impersonationContext?: Api.IImpersonationContext;
	className?: string;
	isOpen?: boolean;
	onRequestClose?(result: AutomationDisableCancelSelection): void;
	styles?: StyleDeclarationValue[];
}

export enum AutomationDisableCancelSelection {
	Disable,
	DisableAndCancelAllInProgress,
	DoNothing,
}

export const AutomationDisableCancelModal: React.FC<IProps> = props => {
	const { automationTemplate, impersonationContext, isOpen, onRequestClose } = props;
	const { logEvent, logApiError } = useEventLogging('AutomationDisableCancelModal');
	const errorMessages = useErrorMessages();

	const updateStatusMutation = useAutomationTemplateSetStatusMutation({
		onError: error => {
			logApiError('DisableAutomation-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const cancelAllInProgressMutation = useCancelAllInProgressAutomationsMutation({
		onError: error => {
			logApiError('CancelAllInProgress-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const [activeSelection, setActiveSelection] = React.useState<AutomationDisableCancelSelection | null>(null);

	const hasInProgress = (automationTemplate?.stats?.inProgress ?? 0) > 0;
	const isBusy = updateStatusMutation.isPending || cancelAllInProgressMutation.isPending;

	const handleSelection = async (selection: AutomationDisableCancelSelection) => {
		if (
			selection === AutomationDisableCancelSelection.Disable ||
			selection === AutomationDisableCancelSelection.DisableAndCancelAllInProgress
		) {
			setActiveSelection(selection);
			try {
				if (selection === AutomationDisableCancelSelection.DisableAndCancelAllInProgress) {
					logEvent('CancelAllInProgress');
					await cancelAllInProgressMutation.mutateAsync({
						id: automationTemplate.id,
						impersonationContext,
					});
				}

				logEvent('DisableAutomation', { templateId: automationTemplate.id });
				await updateStatusMutation.mutateAsync({
					id: automationTemplate.id,
					status: Api.TemplateStatus.Disabled,
					impersonationContext,
				});

				const message =
					selection === AutomationDisableCancelSelection.DisableAndCancelAllInProgress
						? 'Automation template disabled and in progress cancelled.'
						: 'Automation template disabled.';
				toast.success(message);
				onRequestClose?.(selection);
			} catch {
				// Error already handled by mutation error callbacks; keep modal open for retry
				setActiveSelection(null);
			}
			return;
		}

		onRequestClose?.(selection);
	};

	const handleOpenChange = (open: boolean) => {
		if (!open && !isBusy) {
			onRequestClose?.(AutomationDisableCancelSelection.DoNothing);
		}
	};

	return (
		<Dialog open={!!isOpen} onOpenChange={handleOpenChange}>
			<DialogPopup styles={[styleSheet.popup]} showCloseButton={!isBusy}>
				<DialogHeader>
					<DialogTitle>Disable automation?</DialogTitle>
				</DialogHeader>
				{hasInProgress && (
					<DialogBody>
						<p className={css(styleSheet.description)}>
							Contacts that have existing automations in progress can be cancelled or left to run.
						</p>
					</DialogBody>
				)}
				<DialogFooter>
					{hasInProgress ? (
						<>
							<Button
								variant='secondary'
								onClick={() => handleSelection(AutomationDisableCancelSelection.DoNothing)}
								disabled={isBusy}
							>
								Do not disable
							</Button>
							<Button
								variant='primary'
								onClick={() => handleSelection(AutomationDisableCancelSelection.Disable)}
								disabled={isBusy}
								isLoading={activeSelection === AutomationDisableCancelSelection.Disable}
							>
								Disable automation only
							</Button>
							<Button
								variant='destructive'
								onClick={() => handleSelection(AutomationDisableCancelSelection.DisableAndCancelAllInProgress)}
								disabled={isBusy}
								isLoading={activeSelection === AutomationDisableCancelSelection.DisableAndCancelAllInProgress}
							>
								Disable and cancel all in progress
							</Button>
						</>
					) : (
						<>
							<Button
								variant='secondary'
								onClick={() => handleSelection(AutomationDisableCancelSelection.DoNothing)}
								disabled={isBusy}
							>
								Do not disable
							</Button>
							<Button
								variant='primary'
								onClick={() => handleSelection(AutomationDisableCancelSelection.Disable)}
								disabled={isBusy}
								isLoading={activeSelection === AutomationDisableCancelSelection.Disable}
							>
								Yes, disable automation
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
